import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription as rxjsSubscription } from 'rxjs';
import { LightstreamerClient, Subscription } from 'lightstreamer-client-web';
import {
	ConnectionState,
	ILsData,
	ISubscriptionModel,
	LsSchemaType,
	SubscriptionChangeType,
	SubscriptionIsSnapshot,
	SubscriptionMode
} from '@easy-lib/modules/shared/models/ls.model';
import { unloadWindowEvent } from '@easy-lib/tools/event-tool';
import { LsConstants } from '@easy-lib/constants/ls.constants';
import { AccountService } from '@easy-lib/modules/account-activity/services/account.service';
import { BaseService } from '@easy-lib/modules/shared/services/Base/base.service';

@Injectable({
	providedIn: 'root'
})
export class MarketSheetLsService extends BaseService implements OnDestroy {
	private lsClient: any;
	private initializeResult: boolean;
	private firstTimeLsConnect = false;

	private connectionState: ConnectionState = ConnectionState.Disconnected;
	private connectionStateSubject = new BehaviorSubject<ConnectionState>(this.connectionState);
	public connectionState$ = this.connectionStateSubject.asObservable();

	constructor(private accountService: AccountService) {
		super();
		unloadWindowEvent(this.unsubscribeAllAndDisconnect);
	}

	public lsConnectedSubject = new BehaviorSubject<boolean>(this.firstTimeLsConnect);
	public lsConnected$ = this.lsConnectedSubject.asObservable();

	start(): any {
		this.lsClient = new LightstreamerClient(this.environment.setting.marketSheetLsUrl[0], 'lsadapter-conf');
		let customerIsin = this.accountService?.userAccountInfo?.value?.customerIsin;
		if (this.environment.setting.lsUrl.includes('stage')) {
			if (customerIsin === '11290080931413' || customerIsin === '11290069635676') {
				customerIsin = '11290937779334';
			} else if (customerIsin === '11290011810777' || customerIsin === '11290222222220') {
				customerIsin = '11290859862836';
			}
		}

		this.lsClient.connectionDetails.setUser(customerIsin);
		this.lsClient.connectionOptions.setCookieHandlingRequired(true);
		this.lsClient.connectionOptions.setEarlyWSOpenEnabled(false);
		this.lsClient.connectionDetails.setPassword('xxxxx');
		this.lsClient.connect();
		this.lsClient.addListener({
			onServerError: err => {
				this.changeConnectionState(ConnectionState.NotConnected);
				if (!this.environment.production) {
					console.log(err);
				}
			},
			onStatusChange: status => {
				// if (!this.environment.production) {
				// 	console.log(status);
				// }
				try {
					const st = status.split(':');
					if (st.length > 0) {
						if (st[0] === 'CONNECTED') this.changeConnectionState(ConnectionState.Connected);
						else if (st[0] === 'CONNECTING') this.changeConnectionState(ConnectionState.Connecting);
						else if (st[0] === 'DISCONNECTED') this.changeConnectionState(ConnectionState.Disconnected);
						else this.changeConnectionState(ConnectionState.Unknown);
					}
				} catch (e) {
					// if (!this.environment.production) {
					console.log(e);
					// }
					this.changeConnectionState(ConnectionState.Unknown);
				}
			}
		});
		this.initializeResult = true;
	}

	changeConnectionState(state: ConnectionState) {
		this.connectionState = state;
		this.connectionStateSubject.next(state);
		if (!this.firstTimeLsConnect && state === ConnectionState.Connected) {
			this.firstTimeLsConnect = true;
			this.lsConnectedSubject.next(true);
		}
	}

	ngOnDestroy(): void {
		this.disconnect();
	}
	unsubscribeAllAndDisconnect() {
		try {
			this?.unsubscribeAll();
			this?.disconnect();
		} catch (error) {
			console.log(error);
		}
	}

	disconnect() {
		this.lsClient?.disconnect();
	}

	subscribeToLsOnLsConnection(func: () => void): rxjsSubscription {
		const sub = this.lsConnected$.subscribe(state => {
			if (state) {
				func.call(null);
			}
		});
		return sub;
	}

	subscribeToLsBySchema(schemaType: LsSchemaType, elementId?: string): Observable<ILsData> {
		const subscriptionModel = this.getSubscriptionModelBySchema(schemaType, elementId);
		return this.subscriptionToItems(subscriptionModel, elementId);
	}

	private getSubscriptionModelBySchema(schemaType: LsSchemaType, elementId?: string): ISubscriptionModel {
		const topic = LsConstants.LsSchemasTopic.find(a => a.schema === schemaType).topic;
		const subscriptionModel: ISubscriptionModel = {
			mode: LsConstants.RawModeSchemas.indexOf(schemaType) > -1 ? SubscriptionMode.Raw : SubscriptionMode.Merge,
			adaptername: LsConstants.LsAdapterName.find(
				a => a.topic === LsConstants.LsSchemasTopic.find(a => a.schema === schemaType).topic
			).value,
			schema: this.getSchema(LsConstants.LsSchemas.find(a => a.type === schemaType).schema),
			issnapshot:
				LsConstants.SnapshotSchemas.indexOf(schemaType) > -1
					? SubscriptionIsSnapshot.Yes
					: SubscriptionIsSnapshot.No,
			items: [topic + (elementId ? ':' + elementId : '')],
			changeType:
				LsConstants.FullModeChangeSchemas.indexOf(schemaType) > -1
					? SubscriptionChangeType.Full
					: SubscriptionChangeType.Field
		};
		return subscriptionModel;
	}

	private getSchema(schema: any): string[] {
		return Object.keys(schema).map(key => schema[key]);
	}

	private subscriptionToItems(subscriptionModel: ISubscriptionModel, elementId?: string): Observable<ILsData> {
		const sub = new Subscription(subscriptionModel.mode, subscriptionModel.items, subscriptionModel.schema);
		sub.setDataAdapter(subscriptionModel.adaptername);
		sub.setRequestedSnapshot(subscriptionModel.issnapshot);
		const changeFieldSource = new Subject<ILsData>();
		const changeField$ = changeFieldSource.asObservable();

		sub.addListener({
			onItemUpdate: updateInfo => {
				try {
					if (subscriptionModel.changeType === SubscriptionChangeType.Field) {
						updateInfo.forEachChangedField((fieldName, fieldPos, val) => {
							const data: ILsData = {
								// inst: updateInfo?.aE,
								inst: elementId ?? '',
								name: fieldName,
								pos: fieldPos,
								val
							};
							changeFieldSource.next(data);
						});
					} else {
						const d = {};
						subscriptionModel.schema.forEach(element => {
							d[element] = updateInfo.getValue(element);
						});
						const data: ILsData = {
							inst: elementId ?? '',
							name: '',
							pos: 0,
							val: d
						};
						changeFieldSource.next(data);
					}
				} catch (e) {
					console.log(e);
				}
			}
		});

		this.lsClient.subscribe(sub);
		return changeField$;
	}

	unsubscribeAll() {
		try {
			const subs = this.lsClient?.getSubscriptions();
			subs?.forEach(sub => {
				sub?.unsubscribe();
			});
		} catch (error) {
			console.log(error);
		}
	}

	getListOfSubscriptions() {
		const subs = this.lsClient?.getSubscriptions();
		console.log('current subs:', subs);
	}

	unsubscribeSchema(type: LsSchemaType, item?: string): boolean {
		const schema = this.getSchema(LsConstants.LsSchemas.find(a => a.type === type).schema);
		let unsubscribeResult = false;
		if (item) unsubscribeResult = this.unsubscribeItem(item, type, schema);
		else unsubscribeResult = this.unsubscribe(type, schema);
		return unsubscribeResult;
	}

	unsubscribeSymbolbyDynamicSchema(item: string, schema: any): boolean {
		let unsubscribeResult = false;
		unsubscribeResult = this.unsubscribeItem(item, null, schema);

		return unsubscribeResult;
	}

	private unsubscribeItem(itm: string, type: LsSchemaType, schema: any): boolean {
		if (!this.initializeResult) return false;
		if (!itm) return;
		if (!type) return;
		const item = LsConstants.LsSchemasTopic.find(a => a.schema === type).topic + ':' + itm;
		const subs = this.lsClient?.getSubscriptions();

		let isUnsubscribe = false;
		subs.forEach(sub => {
			const subItems = sub.getItems();
			const fields = sub.getFields();
			if (subItems[0] === item && this.isEqual(fields, schema)) {
				try {
					this.lsClient.unsubscribe(sub);
					isUnsubscribe = true;
				} catch (e) {
					// console.log('exception at unsubscribeIsin:' + e);
					isUnsubscribe = false;
				}
			}
		});

		return isUnsubscribe;
	}

	private unsubscribe(type: LsSchemaType, schema: any): boolean {
		if (!this.initializeResult) return false;
		if (!type) return;
		const subs = this.lsClient?.getSubscriptions();

		// console.log("current subs:", subs);
		let isUnsubscribe = false;
		subs.forEach(sub => {
			const fields = sub.getFields();
			if (this.isEqual(fields, schema)) {
				try {
					this.lsClient.unsubscribe(sub);
					isUnsubscribe = true;
				} catch (e) {
					isUnsubscribe = false;
				}
			}
		});
		return isUnsubscribe;
	}

	private isEqual(arr1: string[], arr2: string[]): boolean {
		if (arr1.length !== arr2.length) return false;

		for (const index of arr1) {
			const item = index;
			if (!arr2.includes(item)) {
				return false;
			}
		}
		return true;
	}

	logSubs() {
		const subs = this.lsClient?.getSubscriptions();
		console.log('@@@@ current ls subscriptions', subs);
	}
}
